import * as React from "react"
import { Button, Spinner, Table } from "react-bootstrap"
import { format, parseISO } from "date-fns"
import { BehaviorSubject, concat, of, Subscription } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import TablePagination from "../../TablePagination"
import RandomSelectionList from "../RandomSelectionList"
import GetPoolSelectionHistoryList from "../../../api/Randoms/PoolSelectionHistoryGet"
import PoolSelectionHistory, { PoolSelectionHistoryRow, createPoolSelectionRow } from "../../../api/Randoms/RandomsModel/PoolSelectionHistoryRow"

interface PoolSelectionSearchParams {
  pageNumber: number
  poolId: number
}

const PageSize = 5

interface SelectionHistoryGridProps {
  poolId: number
  poolName: string
  poolProgramId: number
  companyId: number
}

interface SelectionHistoryGridState {
  loading: boolean
  data?: PoolSelectionHistory
  selectionListModal: boolean
  poolSelection?: PoolSelectionHistoryRow
}

export default class SelectionHistoryGrid extends React.PureComponent<SelectionHistoryGridProps, SelectionHistoryGridState> {
  state: SelectionHistoryGridState = {
    loading: false,
    selectionListModal: false,
    data: undefined
  }

  page$ = new BehaviorSubject<PoolSelectionSearchParams>({
    pageNumber: 1,
    poolId: this.props.poolId
  })

  pageSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        GetPoolSelectionHistoryList(this.props.poolId, this.props.poolProgramId, PageSize, params.pageNumber).pipe(
          map((result: PoolSelectionHistory) => ({ data: result, loading: false })))
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: PoolSelectionHistory }) => {
        if (newState.data) {
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      () => {
        this.setState({ loading: false })
      }
    )
  }

  componentDidUpdate(prevProps: SelectionHistoryGridProps, prevState: SelectionHistoryGridState) {
    if (prevProps.poolProgramId !== this.props.poolProgramId) {
      this.page$.next({ poolId: this.props.poolId, pageNumber: 1 })
    }
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  convertDate = (date: string) => {
    if (date) {
      return format(parseISO(date), "MM/dd/yyyy")
    } else {
      return ""
    }
  }

  renderRows = () => {
    if (this.state.data && this.state.data.Records && this.state.data.Records.length) {
      return this.state.data.Records.map((row: PoolSelectionHistoryRow, i: number) =>
        <tr key={i}>
          <td>
            {<Button variant="link" onClick={() => this.showSelectionList(row)} className="p-0">{row.Period}</Button>}
          </td>
          <td className="text-center">{row.PoolSize}</td>
          <td className="text-center">{this.convertDate(row.SelectionDate)}</td>
          <td className="text-center border-left">{row.DrugSelected - row.DrugExcused}</td>
          <td className="text-center">{row.AlcoholSelected - row.AlcoholExcused}</td>
          <td className="text-center border-left">{row.DrugExtra}</td>
          <td className="text-center">{row.AlcoholExtra}</td>
          <td className="text-center border-left">{row.DrugPending}</td>
          <td className="text-center">{row.AlcoholPending}</td>
          <td className="text-center border-left">{row.DrugNotComplete}</td>
          <td className="text-center">{row.AlcoholNotComplete}</td>
          <td className="text-center border-left">{row.DrugCompleted}</td>
          <td className="text-center">{row.AlcoholCompleted}</td>
        </tr>)
    } else {
      return <tr><td colSpan={9}>NO DATA FOUND</td></tr>
    }
  }

  showSelectionList = (poolSelection: PoolSelectionHistoryRow) => this.setState({ selectionListModal: true, poolSelection: poolSelection })

  hideSelectionList = () => this.setState({ selectionListModal: false, poolSelection: undefined })

  render() {
    return <>
      <h5>TESTING PERIOD BREAKDOWN</h5>
      <Table responsive className="table-sm dark">
        <thead>
          <tr className="text-normal">
            <th className="border-bottom-0">PERIOD</th>
            <th className="border-bottom-0 text-center">POOL SIZE</th>
            <th className="border-bottom-0 text-center">SELECTION DATE</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS SELECTED</th>
            <th className="border-bottom-0 text-center" colSpan={2}>EXTRA TESTS</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS PEND COLL</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS INCMPL</th>
            <th className="border-bottom-0 text-center" colSpan={2}>TESTS COMPLETED</th>
          </tr>
          <tr className="text-normal">
            <th className="border-top-0"></th>
            <th className="border-top-0"></th>
            <th className="border-top-0"></th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
            <th className="border-top-0 text-center">DRUG</th>
            <th className="border-top-0 text-center">ALCOHOL</th>
          </tr>
        </thead>
        <tbody>
          {
            this.state.loading ?
              <tr>
                <td className="text-center" colSpan={13}>
                  <Spinner animation="border" variant="secondary" />
                </td>
              </tr> : this.renderRows()
          }
        </tbody>
      </Table>
      {this.state.data && <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />}
      {
          this.state.selectionListModal && this.state.poolSelection &&
          <RandomSelectionList onHide={this.hideSelectionList} show={this.state.selectionListModal} accountId={this.props.companyId}
            poolId={this.props.poolId} poolName={this.props.poolName} poolSelection={createPoolSelectionRow(this.state.poolSelection)} 
            unmaskDonorId={0}/> 
      }

    </>
  }
}
